import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { FaIdCard, FaBuilding, FaBriefcase, FaMapMarkerAlt, FaCalendarAlt, FaPhone, FaEnvelope, FaFileUpload, FaTimes } from 'react-icons/fa';
import { getEmploymentById, updateEmployment } from '../../../services/candidate/api';
import PopupModel from '../../popup-model/PopupModel';
import './NewEmployement.css';

const EditEmployment = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    employeeCode: '',
    companyName: '',
    jobTitle: '',
    location: '',
    workingFrom: '',
    workingTo: '',
    hrContactNumber: '',
    hrContactEmail: '',
    employerReferenceNumber: '',
    uanNumber: '',
  });

  const [files, setFiles] = useState({
    experienceLetter: null,
    offerLetter: null,
    salarySlips: [],
    bankStatement: null,
    form16: null,
    form26: null,
  });

  const [loading, setLoading] = useState(true);   
  const [saving, setSaving] = useState(false); 
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);

  // Helper function to extract file name from URL
  const getFileNameFromUrl = (url) => {
    if (!url) return null;
    try {
      const pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop());
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchEmploymentData = async () => {
      try {
        setLoading(true);
        const data = await getEmploymentById(id);

        setFormData({
          employeeCode: data.employeeCode || '',
          companyName: data.companyName || '',
          jobTitle: data.jobTitle || '',
          location: data.location || '',
          workingFrom: data.workingFrom || '',
          workingTo: data.workingTo || '',
          hrContactNumber: data.hrContactNumber || '',
          hrContactEmail: data.hrContactEmail || '',
          employerReferenceNumber: data.employerReferenceNumber || '',
          uanNumber: data.uanNumber || '',
        });

        setFiles({
          experienceLetter: data.experienceLetterUrl ? { url: data.experienceLetterUrl, name: getFileNameFromUrl(data.experienceLetterUrl) } : null,
          offerLetter: data.offerLetterUrl ? { url: data.offerLetterUrl, name: getFileNameFromUrl(data.offerLetterUrl) } : null,
          salarySlips: data.salarySlipsUrl
            ? data.salarySlipsUrl.split(',').map(url => ({ url: url.trim(), name: getFileNameFromUrl(url.trim()) }))
            : [],
          bankStatement: data.bankStatementUrl ? { url: data.bankStatementUrl, name: getFileNameFromUrl(data.bankStatementUrl) } : null,
          form16: data.form16Url ? { url: data.form16Url, name: getFileNameFromUrl(data.form16Url) } : null,
          form26: data.form26Url ? { url: data.form26Url, name: getFileNameFromUrl(data.form26Url) } : null,
        });
      } catch (err) {
        setError(`Failed to load employment data: ${err.message}`);
        console.error('Error fetching employment data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmploymentData();
  }, [id]);

  const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
      if (dateStr.match(/^\d{4}$/)) {
        return new Date(parseInt(dateStr, 10), 0, 1);
      } else if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleDrop = (e, field) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (field === 'salarySlips') {
      if (droppedFiles.every((file) => file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize)) {
        setFiles((prev) => ({
          ...prev,
          salarySlips: [...prev.salarySlips, ...droppedFiles].slice(0, 3),
        }));
        setValidationErrors((prev) => ({ ...prev, salarySlips: '' }));
      } else {
        setValidationErrors((prev) => ({ ...prev, salarySlips: 'Only PDF files under 5MB are allowed for salary slips' }));
      }
    } else {
      const file = droppedFiles[0];
      if (file && file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize) {
        setFiles((prev) => ({ ...prev, [field]: file }));
        setValidationErrors((prev) => ({ ...prev, [field]: '' }));
      } else {
        setValidationErrors((prev) => ({ ...prev, [field]: `Only PDF files under 5MB are allowed for ${field}` }));
      }
    }
  };

  const handleFileChange = (e, field) => {
    const selectedFiles = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (field === 'salarySlips') {
      if (selectedFiles.every((file) => file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize)) {
        setFiles((prev) => ({
          ...prev,
          salarySlips: [...prev.salarySlips, ...selectedFiles].slice(0, 3),
        }));
        setValidationErrors((prev) => ({ ...prev, salarySlips: '' }));
      } else {
        setValidationErrors((prev) => ({ ...prev, salarySlips: 'Only PDF files under 5MB are allowed for salary slips' }));
      }
    } else {
      const file = selectedFiles[0];
      if (file && file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize) {
        setFiles((prev) => ({ ...prev, [field]: file }));
        setValidationErrors((prev) => ({ ...prev, [field]: '' }));
      } else {
        setValidationErrors((prev) => ({ ...prev, [field]: `Only PDF files under 5MB are allowed for ${field}` }));
      }
    }
  };

  const handleRemoveFile = (field, index = null) => {
    if (field === 'salarySlips') {
      setFiles((prev) => ({
        ...prev,
        salarySlips: prev.salarySlips.filter((_, i) => i !== index),
      }));
    } else {
      setFiles((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'employeeCode':
        if (!value) {
          error = 'Employee code is required';
        } 
        break;
      case 'companyName':
        if (!value) error = 'Company name is required';
        else if (!/^[a-zA-Z0-9 ]+$/.test(value)) {
          error = 'Special characters are not allowed';
        }
        break;
      case 'location':
        if (value && !/^[A-Za-z ,]{1,100}$/.test(value)) {
          error = 'Location can only contain alphabets, spaces, and commas (max 100 characters)';
        }
        break;
      case 'jobTitle':
        if (!value) error = 'Job title is required';
        else if (!/^[A-Za-z ]{1,50}$/.test(value)) error = 'Job title can only contain alphabets and spaces (max 50 characters)';
        break;
      case 'workingFrom':
        if (!value) {
          error = 'Working from date is required';
        } else if (!/^(\d{4}|\d{2}-\d{2}-\d{4})$/.test(value)) {
          error = 'Working from must be in DD-MM-YYYY or YYYY format';
        } else {
          const fromDate = parseDateString(value);
          const toDate = parseDateString(formData.workingTo);
          const today = new Date();

          if (fromDate && fromDate > today) {
            error = 'Working From date cannot be in the future.';
          }
          if (fromDate && toDate && fromDate > toDate) {
            error = 'Working From date cannot be after Working To date.';
          }
        }
        break;
        case 'workingTo':
        if (!value) {
          error = 'Working to date is required';
        } else if (!/^(\d{4}|\d{2}-\d{2}-\d{4})$/.test(value)) {
          error = 'Working to must be in DD-MM-YYYY or YYYY format';
        } else {
          const fromDate = parseDateString(formData.workingFrom);
          const toDate = parseDateString(value);
          const today = new Date();
          if (toDate && toDate > today) {
            error = 'Working To date cannot be in the future.';
          }
          if (fromDate && toDate && fromDate > toDate) {
            error = 'Working From date cannot be after Working To date.';
          }
        }
        break;
      case 'hrContactNumber':
        if (value && !/^\d{10}$/.test(value)) error = 'HR contact number must be 10 digits if provided';
        break;
      case 'hrContactEmail':
        if (!value) error = 'HR contact email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Valid HR contact email is required';
        break;
      case 'employerReferenceNumber':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'EmployerReferenceNumber must be exactly 10 digits';
        }
        break;
      case 'uanNumber':
        if (value && !/^\d{12}$/.test(value)) {
          error = 'UAN must be exactly 12 digits';
        }
        break;
      default:
        break;
    }
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
    return error;
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const fieldError = validateField(key, formData[key]);
      if (fieldError) errors[key] = fieldError;
    });

    // Validate offerLetter (requires either a new non-empty file or an existing URL)
    if (!(files.offerLetter instanceof File ? files.offerLetter.size > 0 : files.offerLetter?.url)) {
      errors.offerLetter = 'Offer letter is required';
    }

    // Validate bankStatement (requires either a new non-empty file or an existing URL)
    if (!(files.bankStatement instanceof File ? files.bankStatement.size > 0 : files.bankStatement?.url)) {
      errors.bankStatement = 'Bank statement is required';
    }

    // Validate salarySlips (requires exactly 3, either new non-empty files or existing URLs)
    const validSalarySlips = files.salarySlips.filter(f => (f instanceof File ? f.size > 0 : f.url)).length;
    if (validSalarySlips !== 3) {
      errors.salarySlips = 'Exactly 3 salary slips are required';
    }

    setValidationErrors((prev) => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  setSubmissionResult(null);
  setShowResultPopup(false);

  if (!validateForm()) {
    setSubmissionResult({
      isSuccess: false,
      title: 'Validation Error',
      message: 'Please fix the errors in the form before submitting.',
    });
    setShowResultPopup(true);
    return;
  }

  setSaving(true);

  const payload = new FormData();

  Object.entries(formData).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      payload.append(key, value);
    }
  });

  if (files.experienceLetter instanceof File) {
    payload.append('experienceLetter', files.experienceLetter);
  }

  if (files.offerLetter instanceof File) {
    payload.append('offerLetter', files.offerLetter);
  }

  if (files.bankStatement instanceof File) {
    payload.append('bankStatement', files.bankStatement);
  }

  if (files.form16 instanceof File) {
    payload.append('form16', files.form16);
  }

  if (files.form26 instanceof File) {
    payload.append('form26', files.form26);
  }

  if (Array.isArray(files.salarySlips)) {
    files.salarySlips.forEach((file) => {
      if (file instanceof File) {
        payload.append('salarySlips', file);
      }
    });
  }

  try {
    const response = await updateEmployment(id, payload);

    if (response?.status === true) {
      setSubmissionResult({
        isSuccess: true,
        title: 'Success',
        message: response.message || 'Employment details updated successfully!',
      });

      setTimeout(() => navigate('/candidate/employment-info'), 1500);
    } else {
      throw new Error(response?.message || 'Update failed');
    }
  } catch (err) {
    setSubmissionResult({
      isSuccess: false,
      title: 'Submission Error',
      message: err.response?.data?.message || 'Failed to update employment details',
    });
  } finally {
    setSaving(false);
    setShowResultPopup(true);
  }
};

  const closeResultPopup = () => {
    setShowResultPopup(false);
    setSubmissionResult(null);
  };

  const renderUploadField = (label, fieldName) => (
    <div className="uploadField">
      <p className="upload-label-title">
        <FaFileUpload className="filed-icons" /> {label}
      </p>
      <div
        className="upload-certificate-button"
        onClick={() => document.getElementById(fieldName).click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => handleDrop(e, fieldName)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            document.getElementById(fieldName).click();
          }
        }}
        aria-describedby={`${fieldName}-description`}
      >
        {fieldName === 'salarySlips'
          ? `Drag & Drop or Click to Upload up to ${3 - files.salarySlips.length} PDFs`
          : 'Drag & Drop or Click to Upload PDF'}
      </div>
      <input
        type="file"
        id={fieldName}
        accept=".pdf"
        multiple={fieldName === 'salarySlips'}
        hidden
        onChange={(e) => handleFileChange(e, fieldName)}
        aria-describedby={`${fieldName}-description`}
      />
      <div className="upload-error-container">
        {validationErrors[fieldName] && (
          <Typography color="error" variant="caption" id={`${fieldName}-description`}>
            {validationErrors[fieldName]}
          </Typography>
        )}
        {(fieldName !== 'salarySlips' && files[fieldName]) && (
          <div className="file-list">
            <span className="file-list-item">
              {files[fieldName] instanceof File ? files[fieldName].name : files[fieldName].name || 'Existing File'}
              <FaTimes
                className="remove-file-icon"
                onClick={() => handleRemoveFile(fieldName)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleRemoveFile(fieldName);
                  }
                }}
                aria-label={`Remove ${files[fieldName] instanceof File ? files[fieldName].name : files[fieldName].name || 'Existing File'}`}
              />
            </span>
          </div>
        )}
        {fieldName === 'salarySlips' && files.salarySlips.length > 0 && (
          <div className="file-list">
            {files.salarySlips.map((file, index) => (
              <span key={index} className="file-list-item">
                {file instanceof File ? file.name : file.name || `Existing File ${index + 1}`}
                <FaTimes
                  className="remove-file-icon"
                  onClick={() => handleRemoveFile('salarySlips', index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleRemoveFile('salarySlips', index);
                    }
                  }}
                  aria-label={`Remove ${file instanceof File ? file.name : file.name || `Existing File ${index + 1}`}`}
                />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderInputField = (label, name, type = 'text', placeholder = '', Icon) => (
    <div className="inputField">
      <p className="upload-label-title">
        {Icon && <Icon className="filed-icons" aria-hidden="true" />} {label}
      </p>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={validationErrors[name] ? 'input-error' : ''}
        aria-describedby={`${name}-error`}
      />
      {validationErrors[name] && (
        <Typography color="error" variant="caption" id={`${name}-error`}>
          {validationErrors[name]}
        </Typography>
      )}
    </div>
  );

  if (error) return <div className="error">{error}</div>;

  return (
    <>
      <h2 className="candidate-titles">Update Employment</h2>
      <Box className="employee-form-container">
       {loading && (
          <Box className="loading-overlay">
            <Box className="loading-content">
              <CircularProgress size={40} style={{ color: '#4D0054' }} />
              <Typography className="loading-text">Loading employment details...</Typography>
            </Box>
          </Box>
        )}
        {saving && (
          <Box className="loading-overlay">
            <Box className="loading-content">
              <CircularProgress size={40} style={{ color: '#4D0054' }} />
              <Typography className="loading-text">Saving...</Typography>
            </Box>
          </Box>
        )}
        <form className="employee-form" onSubmit={handleSubmit}>
          <div className="grid-2col">
            {renderInputField('Employee Code', 'employeeCode', 'text', '', FaIdCard)}
            {renderInputField('Company Name', 'companyName', 'text', '', FaBuilding)}
            {renderInputField('Job Title', 'jobTitle', 'text', '', FaBriefcase)}
            {renderInputField('Location (optional)', 'location', 'text', '', FaMapMarkerAlt)}
            {renderInputField('Working From* (DD-MM-YYYY or YYYY)', 'workingFrom', 'text', 'DD-MM-YYYY or YYYY', FaCalendarAlt)}
            {renderInputField('Working To* (DD-MM-YYYY or YYYY)', 'workingTo', 'text', 'DD-MM-YYYY or YYYY', FaCalendarAlt)}
            {renderInputField('HR Contact Number (10 digits)', 'hrContactNumber', 'tel', '', FaPhone)}
            {renderInputField('HR Contact Email*', 'hrContactEmail', 'email', '', FaEnvelope)}
            {renderInputField('Employer Reference / CoWorker Number (optional)', 'employerReferenceNumber', 'text', '', FaIdCard)}
            {renderInputField('Universal Account Number (UAN) (optional)', 'uanNumber', 'text', '', FaIdCard)}
          </div>
          <div className="grid-2col file-grid">
            {renderUploadField('Experience Letter (PDF Only)(optional)', 'experienceLetter')}
            {renderUploadField('Offer Letter (PDF Only)', 'offerLetter')}
            {renderUploadField('Salary Slips (Exactly 3 PDFs)', 'salarySlips')}
            {renderUploadField('Bank Statement (PDF Only)', 'bankStatement')}
            {renderUploadField('Form 16 (PDF Only)(optional)', 'form16')}
            {renderUploadField('Form 26 (PDF Only)(optional)', 'form26')}
          </div>
          <div className="emp-form-actions">
            <button className="backbutton" type="button" onClick={() => navigate('/candidate/employment-info')}>
              Back
            </button>
            <button type="submit" className="stepHighSchoolButton" disabled={saving}>
              {saving ? 'Saving...' : 'Update Details'}
            </button>
          </div>
        </form>
        {showResultPopup && (
          <PopupModel
            show={showResultPopup}
            onClose={closeResultPopup}
            title={submissionResult?.title}
            message={submissionResult?.message}
            isSuccess={submissionResult?.isSuccess}
          />
        )}
      </Box>
    </>
  );
};

export default EditEmployment;